import { type OperationVariables } from '@apollo/react-common';
import { type DataProxy } from 'apollo-cache';
import cloneDeepWith from 'lodash/cloneDeepWith';
import { type GraphQLTaggedNode } from 'react-relay';
import { type ConcreteRequest, createOperationDescriptor, FRAGMENT_OWNER_KEY } from 'relay-runtime';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { type QueryCacheBridgeSource } from './cacheBridge/queryCacheBridgeSync.ts';
import { getRelayOperationName } from './relayOperation.ts';

const CLIENT_EDGE_TRAVERSAL_PATH_KEY = '__clientEdgeTraversalPath';

function cloneRelayQueryResult<TData>(data: TData): TData {
	return cloneDeepWith(data, (value: unknown, key: number | string | undefined) => {
		// Relay fragment refs can contain operation descriptors and client-edge reader nodes.
		// Keep that internal metadata by reference while restoring Apollo-compatible mutable data.
		if (key === FRAGMENT_OWNER_KEY || key === CLIENT_EDGE_TRAVERSAL_PATH_KEY) {
			return value;
		}

		return undefined;
	});
}

export type ReadQueryInteropArgs<TVariables extends Record<string, any> = OperationVariables> =
	Pick<DataProxy.Query<TVariables>, 'variables'> & {
		query: GraphQLTaggedNode;
		/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
		__callerPackage__?: string;
	};

export function readQueryInterop<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: ReadQueryInteropArgs<TVariables>): TData | null {
	const operationName = getRelayOperationName(params.query);
	const source: QueryCacheBridgeSource = isRelayEnabled(params.__callerPackage__, {
		operationName,
		phase: 'cache',
	})
		? 'relay'
		: 'apollo';

	// Cache reads stay native to the selected route. The cache bridge only
	// mirrors successful writes/results into the other cache.
	return readQueryInteropOriginal<TData, TVariables>(params, source);
}

function readQueryInteropOriginal<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(params: ReadQueryInteropArgs<TVariables>, source: QueryCacheBridgeSource): TData | null {
	return source === 'relay'
		? readQueryInteropRelay<TData, TVariables>(params)
		: readQueryInteropApollo<TData, TVariables>(params);
}

function readQueryInteropRelay<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: ReadQueryInteropArgs<TVariables>,
): TData | null {
	const relayEnv = typeof window !== 'undefined' ? window.GLOBAL_RELAY_ENV : undefined;
	if (!relayEnv) {
		throw new Error(
			`[Relay migration] Missing GLOBAL_RELAY_ENV for readQueryInterop; ${params.__callerPackage__}.`,
		);
	}

	const queryNode = params.query as ConcreteRequest;
	if (!queryNode?.fragment || !queryNode?.operation) {
		return null;
	}

	const variables: Record<string, any> = (params.variables as Record<string, any>) ?? {};
	const operation = createOperationDescriptor(queryNode, variables);
	const snapshot = relayEnv.lookup(operation.fragment);

	return snapshot.isMissingData ? null : cloneRelayQueryResult(snapshot.data as TData);
}

function readQueryInteropApollo<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: ReadQueryInteropArgs<TVariables>,
): TData | null {
	return window['GLOBAL_APOLLO_CLIENT']!.readQuery<TData, TVariables>({
		...params,
		query: toDocumentNode(params.query),
	});
}
